// Adapted from smoothchart's price-chart.tsx.
// Strips Tailwind and hip4-sdk references; inlines styles using tokens.css
// variables. Pure visx + React 19.

import {
  curveCatmullRom,
  curveLinear,
  curveMonotoneX,
  curveNatural,
  curveStep,
} from "@visx/curve";
import { ParentSize } from "@visx/responsive";
import {
  Axis,
  DataContext,
  Grid,
  LineSeries,
  type GlyphProps,
  Tooltip,
  XYChart,
} from "@visx/xychart";
import { useCallback, useContext, useMemo } from "react";
import { ActiveGlyph } from "./active-glyph";
import { buildHedgeChartTheme } from "./chart-theme";
import { ChartTooltip } from "./chart-tooltip";
import { formatSigFig } from "./format";
import { LiveDot } from "./live-dot";
import { PriceChartSkeleton } from "./price-chart-skeleton";

export interface PriceChartDatum {
  readonly time: number;
  readonly value: number;
}

interface PriceChartMargin {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export type PriceChartRefLine = {
  value: number;
  color: string;
  label?: string;
  dash?: string;
  /** Label alignment along the line. Default: "right". */
  labelAlign?: "left" | "right";
};

interface PriceChartProps {
  readonly data: ReadonlyArray<PriceChartDatum>;
  readonly lineColor?: string;
  readonly assetName?: string;
  readonly valueFormatter?: (v: number) => string;
  readonly xTickFormatter?: (v: Date | number) => string;
  readonly yTickFormatter?: (v: number) => string;
  readonly yAxisPosition?: "left" | "right";
  readonly showGrid?: boolean;
  readonly showTooltip?: boolean;
  readonly showXAxis?: boolean;
  readonly curveType?: "monotoneX" | "natural" | "linear" | "step" | "catmullRom";
  readonly numXTicks?: number;
  readonly numYTicks?: number;
  readonly margin?: PriceChartMargin;
  readonly xDomain?: readonly [Date, Date] | undefined;
  readonly yDomain?: readonly [number, number] | undefined;
  readonly currentPrice?: number | undefined;
  readonly showLiveDot?: boolean;
  /** Horizontal reference lines (entry, liq, breakeven). */
  readonly refLines?: ReadonlyArray<PriceChartRefLine>;
}

function HorizontalRefLine({
  value,
  color,
  label,
  dash = "4 4",
  labelAlign = "right",
}: PriceChartRefLine) {
  const { yScale, margin, innerWidth } = useContext(DataContext);
  if (!yScale || !margin) return null;
  const fn = yScale as unknown as (v: number) => number;
  const y = fn(value);
  if (y === undefined || Number.isNaN(y)) return null;
  const x1 = margin.left;
  const x2 = margin.left + (innerWidth ?? 0);
  return (
    <g>
      <line
        x1={x1}
        x2={x2}
        y1={y}
        y2={y}
        stroke={color}
        strokeDasharray={dash}
        strokeWidth={1}
        strokeOpacity={0.8}
      />
      {label && (
        <text
          x={labelAlign === "right" ? x2 - 4 : x1 + 4}
          y={y - 3}
          fill={color}
          fontFamily="var(--f-mono, monospace)"
          fontSize={9}
          textAnchor={labelAlign === "right" ? "end" : "start"}
          letterSpacing="0.14em"
        >
          {label}
        </text>
      )}
    </g>
  );
}

const DEFAULT_MARGIN: PriceChartMargin = {
  top: 14,
  right: 58,
  bottom: 28,
  left: 14,
};

const CURVE_MAP = {
  monotoneX: curveMonotoneX,
  natural: curveNatural,
  linear: curveLinear,
  step: curveStep,
  catmullRom: curveCatmullRom.alpha(0.5),
} as const;

function defaultFormatPrice(v: number): string {
  return `$${formatSigFig(v)}`;
}

function xAccessor(d: PriceChartDatum): Date {
  if (!d) return new Date(0);
  return new Date(d.time);
}

function yAccessor(d: PriceChartDatum): number {
  if (!d) return 0;
  return d.value;
}

function computeYDomain(
  data: ReadonlyArray<PriceChartDatum>,
): [number, number] {
  if (data.length === 0) return [0, 1];
  const values = data.map((d) => d.value);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const range = rawMax - rawMin || Math.abs(rawMax) * 0.01 || 1;
  const padding = range * 0.1;
  return [rawMin - padding, rawMax + padding];
}

export function PriceChart({
  data,
  lineColor = "#00ffff",
  assetName,
  valueFormatter = defaultFormatPrice,
  xTickFormatter,
  yTickFormatter,
  yAxisPosition = "right",
  showGrid = true,
  showTooltip = true,
  showXAxis = true,
  curveType = "monotoneX",
  numXTicks = 6,
  numYTicks = 5,
  margin = DEFAULT_MARGIN,
  xDomain,
  yDomain: yDomainProp,
  currentPrice,
  showLiveDot = true,
  refLines,
}: PriceChartProps) {
  const curve = CURVE_MAP[curveType];
  const effectiveYTickFormatter = yTickFormatter ?? valueFormatter;

  const theme = useMemo(() => buildHedgeChartTheme(lineColor), [lineColor]);

  const paddedXDomain = useMemo(() => {
    if (xDomain || data.length < 2) return undefined;
    const first = data[0]!.time;
    const last = data[data.length - 1]!.time;
    const range = last - first;
    const pad = range * 0.05;
    return [new Date(first), new Date(last + pad)] as const;
  }, [data, xDomain]);

  const effectiveXDomain = xDomain ?? paddedXDomain;

  const computedYDomain = useMemo(() => computeYDomain(data), [data]);

  const effectiveYDomain = useMemo(() => {
    if (yDomainProp) return yDomainProp;
    if (data.length === 0) return computedYDomain;

    const last = data[data.length - 1]!.value;
    const ref = currentPrice ?? last;
    const step =
      ref < 1 ? 0.05 : ref < 10 ? 0.5 : ref < 100 ? 2 : ref < 1000 ? 20 : 200;

    let min = Infinity;
    let max = -Infinity;
    for (const d of data) {
      if (d.value < min) min = d.value;
      if (d.value > max) max = d.value;
    }
    const low = min < computedYDomain[0] ? min - step : computedYDomain[0];
    const high = max > computedYDomain[1] ? max + step : computedYDomain[1];
    return [low, high] as const;
  }, [yDomainProp, computedYDomain, data, currentPrice]);

  const renderGlyph = useCallback(
    ({ x, y }: GlyphProps<PriceChartDatum>) => (
      <ActiveGlyph x={x} y={y} color={lineColor} />
    ),
    [lineColor],
  );

  if (data.length === 0) {
    return (
      <PriceChartSkeleton
        margin={margin}
        numYTicks={numYTicks}
        numXTicks={numXTicks}
      />
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <ParentSize>
        {({ width, height }) => {
          if (width === 0 || height === 0) return null;

          const clipId = `chart-clip-${assetName ?? "x"}`;
          const plotLeft = margin.left;
          const plotTop = margin.top;
          const plotWidth = width - margin.left - margin.right;
          const plotHeight = height - margin.top - margin.bottom;

          return (
            <XYChart
              theme={theme}
              width={width}
              height={height}
              margin={margin}
              xScale={{
                type: "time",
                ...(effectiveXDomain
                  ? { domain: [...effectiveXDomain] as [Date, Date] }
                  : {}),
              }}
              yScale={{
                type: "linear",
                domain: [...effectiveYDomain],
                zero: false,
              }}
            >
              <defs>
                <clipPath id={clipId}>
                  <rect
                    x={plotLeft}
                    y={plotTop}
                    width={plotWidth}
                    height={plotHeight}
                  />
                </clipPath>
              </defs>

              {showGrid && (
                <Grid
                  columns={false}
                  numTicks={numYTicks}
                  lineStyle={{ stroke: "rgba(194,214,255,0.06)" }}
                />
              )}

              <g clipPath={`url(#${clipId})`}>
                <LineSeries
                  dataKey="price"
                  data={data as PriceChartDatum[]}
                  xAccessor={xAccessor}
                  yAccessor={yAccessor}
                  stroke={lineColor}
                  strokeWidth={1.75}
                  curve={curve}
                />
              </g>

              {refLines?.map((r, i) => (
                <HorizontalRefLine key={`${r.label ?? "ref"}-${i}`} {...r} />
              ))}

              {showXAxis && (
                <Axis
                  orientation="bottom"
                  numTicks={numXTicks}
                  {...(xTickFormatter ? { tickFormat: xTickFormatter } : {})}
                  hideTicks
                  hideAxisLine
                />
              )}

              <Axis
                orientation={yAxisPosition}
                numTicks={numYTicks}
                tickFormat={effectiveYTickFormatter}
                hideTicks
                hideAxisLine
                tickLabelProps={() => ({
                  dx: yAxisPosition === "right" ? 4 : -4,
                  fontSize: 11,
                })}
              />

              {showTooltip && (
                <Tooltip
                  showVerticalCrosshair
                  verticalCrosshairStyle={{
                    stroke: `${lineColor}55`,
                    strokeWidth: 1,
                  }}
                  showDatumGlyph
                  renderGlyph={renderGlyph}
                  snapTooltipToDatumX
                  snapTooltipToDatumY
                  unstyled
                  applyPositionStyle
                  renderTooltip={({ tooltipData }) => {
                    const datum = tooltipData?.nearestDatum?.datum as
                      | PriceChartDatum
                      | undefined;
                    if (!datum) return null;
                    return (
                      <ChartTooltip
                        assetName={assetName ?? ""}
                        value={valueFormatter(datum.value)}
                        color={lineColor}
                      />
                    );
                  }}
                />
              )}

              <g clipPath={`url(#${clipId})`}>
                {showLiveDot && <LiveDot data={data} color={lineColor} />}
              </g>
            </XYChart>
          );
        }}
      </ParentSize>
    </div>
  );
}
